/**
 * useActivities Hook
 * Manages activity data fetching with search and pagination
 */

import { useState, useCallback, useEffect } from 'react';
import { activityService, ActivityDashboard } from '@/services/features/ActivityService';
import { useAuth } from '@/contexts/AuthContext';

export interface UseActivitiesResult {
  activities: ActivityDashboard[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  entriesPerPage: number;
  searchValue: string;
  setCurrentPage: (page: number) => void;
  setEntriesPerPage: (entries: number) => void;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  clearSearch: () => void;
  refetch: () => void;
}

export const useActivities = (): UseActivitiesResult => {
  const { hasuraToken } = useAuth();
  const [activities, setActivities] = useState<ActivityDashboard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [activeSearch, setActiveSearch] = useState<string | null>(null);

  const totalPages = Math.ceil(totalCount / entriesPerPage);

  const fetchActivities = useCallback(async () => {
    if (!hasuraToken) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const offset = (currentPage - 1) * entriesPerPage;
      
      let result;
      if (activeSearch && activeSearch.trim()) {
        result = await activityService.searchActivities(
          activeSearch,
          offset,
          entriesPerPage,
          hasuraToken
        );
      } else {
        result = await activityService.getActivities(
          offset,
          entriesPerPage,
          hasuraToken
        );
      }

      setActivities(result.activities);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch activities');
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, [hasuraToken, currentPage, entriesPerPage, activeSearch]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleSearch = useCallback(() => {
    setActiveSearch(searchValue);
    setCurrentPage(1);
  }, [searchValue]);

  const clearSearch = useCallback(() => {
    setSearchValue('');
    setActiveSearch(null);
    setCurrentPage(1);
  }, []);

  const handleSetCurrentPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const handleSetEntriesPerPage = useCallback((entries: number) => {
    setEntriesPerPage(entries);
    setCurrentPage(1);
  }, []);

  return {
    activities,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    entriesPerPage,
    searchValue,
    setCurrentPage: handleSetCurrentPage,
    setEntriesPerPage: handleSetEntriesPerPage,
    setSearchValue,
    handleSearch,
    clearSearch,
    refetch: fetchActivities
  };
};
